'use client';

import { useEffect, useMemo, useState } from 'react';

import {
    Align,
    Column,
    Columns,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { ShortListContext } from '@app-types/eligibility';
import { UpsellModalComponentType } from '@app-types/eligibility/config';
import { FaiTabsEnum, TabConfig } from '@app-types/home-fai';
import { OffersByCategoryCode } from '@app-types/plan';

import { getUpsellCombination, getUpsellOffer } from '@helpers/fai-upsell';
import { formatTagText, sendPageViewTracking, sendTracking } from '@helpers/tracking';

import { TRACKING_TYPE_SEARCH, TYPE_ELIGIBILITY } from '@constants/eligibility';
import { ERROR_CONTEXT, TECHNOLOGY } from '@constants/provider';

import {
    EligibilityOffer,
    EligibilityResults,
    JourneyConfigType,
    getXgboxOffersDetails,
    sendTrackingAddFaiToCart,
} from '@eligibility/helpers';

import { EditAddress } from './edit-address';
import { EligibilityResultError } from './eligibility-result-error';
import { EligibilityResultTabs } from './eligibility-result-tabs';

interface EligibilityResultProps {
    type: TYPE_ELIGIBILITY;
    results: EligibilityResults;
    onResetAddress?: () => void;
    journeyConfig: JourneyConfigType;
    addToCart: (
        gencodes: string[],
        idPanierCorbis: string,
        redirect?: boolean,
        callback?: () => void,
        homeZone?: string,
    ) => Promise<void>;
    shortList?: ShortListContext;
    openOfferUpsellModal: (
        clickedOffer: EligibilityOffer,
        upsellOffer: EligibilityOffer,
        eligibilityResults: EligibilityResults,
        modal?: UpsellModalComponentType,
        isThreeClicsFtth?: boolean,
    ) => void;
    offersByCategory: OffersByCategoryCode;
    planId?: string;
    tabConfig: TabConfig;
    defaultTab: FaiTabsEnum;
}

// Exposed so it can be reused elsewhere on the page
const getTrackingSearchType = (type: TYPE_ELIGIBILITY) => {
    switch (type) {
        case TYPE_ELIGIBILITY.SEARCH:
            return TRACKING_TYPE_SEARCH.ADDRESS;
        case TYPE_ELIGIBILITY.PTO:
            return TRACKING_TYPE_SEARCH.PTO;
        default:
            return undefined;
    }
};

const sendEligibilityResultsTracking = (
    type: TYPE_ELIGIBILITY,
    results: EligibilityResults,
    journeyConfig: JourneyConfigType,
) => {
    const tracking = journeyConfig.tracking;

    const eligibleTechnos = Object.entries(results.offers).reduce((acc, [technology, offers]) => {
        if (offers.length > 0) {
            acc.push(technology);
        }
        return acc;
    }, [] as string[]);

    const productsTag = Object.values(results.offers)
        .flat()
        .map((offer) => {
            const offerPrice: string = `${offer.price + (offer.rental_fees || 0) - (offer.promotion?.amount || 0)}`;

            return {
                id: offer.sku,
                name: offer.name,
                gencode: offer.sku,
                sku: offer.sku,
                type_id: offer.type,
                rangeNg: offer.rangeNg,
                commercialSeries: offer.commercialSeries,
                price: +Number.parseFloat(offerPrice).toFixed(2),
                technology: offer.technology,
                network: 'N/A',
                play: offer.play,
            };
        });

    sendPageViewTracking(false, {
        page: 'Eligibilite_resultats',
        pageCategory: tracking.pageCategory,
        eCommerceStep: 'Liste_produits',
        universe: tracking.universe,
        site:
            results.lmPartner && !results.lmPartner.defaultTrackingSite
                ? `Boutique_${results.lmPartner.value}`
                : undefined,
        variant: {
            ...tracking?.variant,
            etape_eligibilite: 'Eligibilite_resultats',
            eligibleOffresFAI: (Object.values(results.offers).length > 0).toString(),
            onglet: window.location.hash.replace('#', ''),
        },
        variables: {
            ...tracking?.variables,
            eligibilite_techno_technique: 'FTTH, ADSL, VDSL, BOX_4G, BOX_5G',
            eligibilite_techno_resultats: eligibleTechnos.join(','),
            testParAdresse: getTrackingSearchType(type),
        },
        products: productsTag,
    });
};

export function EligibilityResult({
    type,
    results,
    onResetAddress,
    addToCart,
    journeyConfig,
    shortList,
    openOfferUpsellModal,
    offersByCategory,
    planId,
    tabConfig,
    defaultTab,
}: Readonly<EligibilityResultProps>) {
    const [address, setAddress] = useState(results.address.inlineSplit);

    const checkUpsellOffer = async (sku: string, shouldAddToCart: boolean) => {
        const allResultsOffers = Object.values(results.offers).flat();
        const clickedOffer = allResultsOffers.find((offer) => offer.sku === sku)!;
        const upsellOffer = getUpsellOffer(allResultsOffers, clickedOffer, journeyConfig.productsToUpsell);

        if (upsellOffer) {
            const upsellCombination = getUpsellCombination(clickedOffer, journeyConfig.productsToUpsell)!;
            openOfferUpsellModal(clickedOffer, upsellOffer, results, upsellCombination.modalComponentType);
        } else if (shouldAddToCart) {
            const autoAdd = clickedOffer?.neededElements?.map((el) => el.gencode) ?? [];
            const homeZone =
                clickedOffer.technology === TECHNOLOGY.BOX_5G
                    ? results.results.box5g.homeZone
                    : results.results.box4g.homeZone;
            await addToCart(
                [clickedOffer.sku, ...autoAdd],
                results.idPanierCorbis,
                true,
                () =>
                    sendTrackingAddFaiToCart(
                        journeyConfig,
                        `Ajouter_au_panier_${formatTagText(clickedOffer?.name)}`,
                        clickedOffer?.technology === TECHNOLOGY.FTTH,
                    ),
                clickedOffer.rangeNg === 'xgbox' ? homeZone : undefined,
            );
        }
    };

    const handleAddToCart = (gencode: string) => {
        if (Boolean(journeyConfig.productsToUpsell)) {
            void checkUpsellOffer(gencode, true);
        } else {
            const allResultsOffers = Object.values(results.offers).flat();
            const offer = allResultsOffers?.find((o) => o.sku === gencode);
            const autoAdd = offer?.neededElements?.map((el) => el.gencode) ?? [];
            const homeZone =
                offer?.technology === TECHNOLOGY.BOX_5G
                    ? results.results.box5g.homeZone
                    : results.results.box4g.homeZone;
            void addToCart(
                [gencode, ...autoAdd],
                results.idPanierCorbis,
                true,
                () =>
                    sendTrackingAddFaiToCart(
                        journeyConfig,
                        `Ajouter_au_panier_${formatTagText(offer?.name)}`,
                        offer?.technology === TECHNOLOGY.FTTH,
                    ),
                offer?.rangeNg === 'xgbox' ? homeZone : undefined,
            );
        }
    };

    const onTabChange = () => {
        sendEligibilityResultsTracking(type, results, journeyConfig);
    };

    const shouldDisplayDefaultMessage = useMemo(() => {
        if (!shortList || shortList.focus) {
            return true;
        }

        // This case appear when the direct to cart fails
        return (
            results.offers[shortList.technology.toUpperCase() as TECHNOLOGY]?.some((offer) => {
                return offer.isSameRangeAndPlay(shortList.rangeNgCommercialSeries, '');
            }) ?? false
        );
    }, [shortList, results]);

    const getAddressByEligType = (type: TYPE_ELIGIBILITY): string[] => {
        const address = [];
        if (type === TYPE_ELIGIBILITY.PTO) {
            address.push(results.address.pto!);
        }

        if (address.length && !!results.address.inline.trim()) {
            address.push('|');
        }

        return address.concat(results.address.inlineSplit);
    };

    useEffect(() => {
        if (location.search.includes('gamme')) {
            sendTracking(false, {
                action: 'Clic_impression',
                category: 'Resultat_eligibilite_direct_panier',
                name: results.error === ERROR_CONTEXT.ERROR ? 'Direct_panier_erreur_technique' : 'Direct_panier_KO',
            });
        }
        sendEligibilityResultsTracking(type, results, journeyConfig);
        setAddress(getAddressByEligType(type));
    }, [type, results, journeyConfig]);

    const { has5gOffer } = getXgboxOffersDetails(results, journeyConfig.offerCategoryCodeToShow);

    return (
        <>
            <Columns align={Align.CENTER}>
                <Column>
                    {!results.error ? (
                        <Rows gap={2}>
                            {shouldDisplayDefaultMessage ? null : (
                                <Row>
                                    <Title level={TitleLevels.FOUR} className='has-text-centered'>
                                        La {shortList!.name}
                                        {shortList!.technology.toUpperCase() === TECHNOLOGY.FTTH
                                            ? ' avec la Fibre '
                                            : ' '}
                                        n&apos;est pas disponible chez vous, mais vous avez accès à d&apos;autres offres
                                        &nbsp;!
                                    </Title>
                                </Row>
                            )}
                            <Row>
                                <EditAddress onResetAddress={onResetAddress} address={address} />
                                <Spacer size={SpacerSize.FIVE} />
                                {results.technology &&
                                    [TECHNOLOGY.BOX_4G, TECHNOLOGY.BOX_5G].includes(results.technology) && (
                                        <Text
                                            level={TextLevels.ONE}
                                            typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                                        >
                                            Vous pouvez profiter de la {has5gOffer ? '5G' : '4G'} box{' '}
                                            {has5gOffer ? 'très' : ''} haut débit
                                        </Text>
                                    )}
                            </Row>
                        </Rows>
                    ) : (
                        <EligibilityResultError
                            results={results}
                            error={results.error}
                            address={
                                <>
                                    <EditAddress onResetAddress={onResetAddress} address={address} />
                                    <Spacer size={SpacerSize.EIGHT} />
                                </>
                            }
                            planId={planId}
                            onAddToCart={handleAddToCart}
                            journeyConfig={journeyConfig}
                        />
                    )}
                </Column>
            </Columns>
            <Columns align={Align.CENTER}>
                <Column desktopSize={10}>
                    <EligibilityResultTabs
                        journeyConfig={journeyConfig}
                        results={results}
                        shortList={shortList}
                        offersByCategory={offersByCategory}
                        onAddToCart={handleAddToCart}
                        tabConfig={tabConfig}
                        defaultFaiTab={defaultTab}
                        onTabChange={onTabChange}
                    />
                </Column>
            </Columns>
        </>
    );
}
